import React, { type ComponentProps, useCallback, useEffect, useRef } from 'react';
import { Tabs, useGlobalSearchParams, usePathname, useRouter, useSegments } from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet, type ViewInstance } from 'react-native';
import Animated, {
  Easing,
  FadeIn,
  FadeInLeft,
  FadeOut,
  LinearTransition,
  useReducedMotion,
  useSharedValue,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';
import { useToggleState } from '@garnish/core';
import { Navbar, NavbarBackLink } from '@garnish/navigation';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useBagCount, useIsLiquidGlassEnabled } from '../../../../hooks';
import { useNavigateBack } from '../../../../navigation';
import {
  MENU_VIEWS,
  NAVBAR_GLASS_FILL_OPACITY,
  NAVBAR_TAB_NAMES,
  TABS,
} from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';
import { NavbarBagButton } from '../NavbarBagButton';
import { NavbarLink } from '../NavbarLink';
import { NavbarStartMenu } from '../NavbarStartMenu';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarXS = (props: Props) => {
  const { tabBarProps } = props;

  const router = useRouter();
  const segments = useSegments();
  const currentPath = usePathname();
  const { category, view } = useGlobalSearchParams<MenuSearchParams>();
  const navigateBack = useNavigateBack();
  const isReduceMotionEnabled = useReducedMotion();
  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { bagCount } = useBagCount();

  // ─── Animated Values ────────────────────────────────────────────────

  const activeTabIndexSV = useSharedValue(
    getActiveTabIndex(
      getActiveTabName({
        segments,
        stateActiveTabName: tabBarProps.state.routes[tabBarProps.state.index]?.name,
      }),
    ),
  );

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const startMenuButtonRef = useRef<ViewInstance>(null);

  // ─── State ─────────────────────────────────────────────────────────────────

  const {
    value: isStartMenuOpen,
    toggleOn: openStartMenu,
    toggleOff: closeStartMenu,
  } = useToggleState();

  // ─── Derived ────────────────────────────────────────────────────────

  const stateActiveRoute = tabBarProps.state.routes[tabBarProps.state.index];
  const stateActiveTabName = stateActiveRoute?.name;
  const activeTabName = getActiveTabName({ segments, stateActiveTabName });

  const isHomeTabActive = activeTabName === TABS.HOME;
  const isAccountTabActive = activeTabName === TABS.ACCOUNT;
  const isRewardsTabActive = activeTabName === TABS.REWARDS;
  const isMenuTabActive = activeTabName === TABS.MENU;
  const isProductMenuView = view === MENU_VIEWS.MENU;
  const hasCategoryParam = category !== undefined;

  const isAccountMenuRoute = segments.length === 3 && segments[1] === TABS.ACCOUNT && segments[2] === TABS.MENU_SINGLE; // prettier-ignore
  const isTopLevelTabRoute = segments.length === 2 || isAccountMenuRoute;
  const activeStackRouteCount = getNestedRouteCount(stateActiveRoute);
  const shouldReturnToAccount = isAccountTabActive && activeStackRouteCount <= 1;
  const shouldAnimateNavbar = !isReduceMotionEnabled;
  const shouldRenderCompactNavbar = (segments.length >= 3 && !isTopLevelTabRoute && !isMenuTabActive) || (isMenuTabActive && (hasCategoryParam || isProductMenuView)) || isRewardsTabActive; // prettier-ignore
  const hasActiveBag = bagCount > 0;

  const shouldRenderOrderCta = !hasActiveBag && isTopLevelTabRoute && (isHomeTabActive || isAccountTabActive); // prettier-ignore
  const shouldRenderStartMenu = isGroupOrderingEnabled && shouldRenderOrderCta;
  const isStartMenuVisible = shouldRenderStartMenu && isStartMenuOpen;
  const orderCtaMessage = getOrderCtaMessage({ isGroupOrderingEnabled, isStartMenuVisible });

  const routes = shouldRenderCompactNavbar
    ? tabBarProps.state.routes.filter(checkIfHomeRoute)
    : tabBarProps.state.routes;
  const navbarRoutes = routes.filter(checkIfNavbarRoute);

  const activeBubbleItemCount = shouldRenderCompactNavbar ? undefined : navbarRoutes.length;

  // ─── Handlers ───────────────────────────────────────────────────────

  const handleBackPress = useCallback(() => {
    track('back_selected', { currentPath });

    if (shouldReturnToAccount) {
      router.dismissTo('/account');
      return;
    }

    if (isMenuTabActive && (hasCategoryParam || isProductMenuView)) {
      router.setParams({ category: undefined, view: MENU_VIEWS.CATEGORIES });
      return;
    }

    navigateBack();
  }, [currentPath, hasCategoryParam, isMenuTabActive, isProductMenuView, navigateBack, router, shouldReturnToAccount, track]); // prettier-ignore

  const handleHomePress = useCallback(() => {
    track('nav_bar_home_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.HOME });
  }, [tabBarProps, track]);

  const handleMenuPress = useCallback(() => {
    track('nav_bar_menu_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.MENU });
  }, [tabBarProps, track]);

  const handleAccountPress = useCallback(() => {
    track('nav_bar_account_selected');
    navigateToTab({ ...tabBarProps, tabName: TABS.ACCOUNT });
  }, [tabBarProps, track]);

  const handleStartOrderPress = useCallback(() => {
    if (isStartMenuVisible) {
      closeStartMenu();
      return;
    }

    track('nav_bar_start_order_selected');

    if (isGroupOrderingEnabled) {
      openStartMenu();
      return;
    }

    navigateToTab({ ...tabBarProps, tabName: TABS.MENU });
  }, [closeStartMenu, isGroupOrderingEnabled, isStartMenuVisible, openStartMenu, tabBarProps, track]); // prettier-ignore

  // ─── Effects ────────────────────────────────────────────────────────

  useEffect(() => {
    activeTabIndexSV.value = getActiveTabIndex(activeTabName);
  }, [activeTabIndexSV, activeTabName]);

  // NOTE: Leaving the page or hiding the CTA closes the start menu, so it never
  //       reappears on its own when the CTA comes back.
  useEffect(closeStartMenu, [closeStartMenu, currentPath, shouldRenderStartMenu]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Navbar.Container isLiquidGlassEnabled={isLiquidGlassEnabled}>
        <Navbar.LinksGroup
          activeBubbleItemCount={activeBubbleItemCount}
          activeTabIndexSV={activeTabIndexSV}
          glassFillOpacity={NAVBAR_GLASS_FILL_OPACITY}
          isLiquidGlassEnabled={isLiquidGlassEnabled}
          layout={shouldAnimateNavbar ? NAVBAR_LAYOUT_TRANSITION : undefined}
          style={shouldRenderCompactNavbar ? undefined : styles.linksGroup}
        >
          {shouldRenderCompactNavbar ? (
            <Animated.View
              key="back"
              entering={shouldAnimateNavbar ? NAVBAR_BACK_ENTERING : undefined}
              exiting={shouldAnimateNavbar ? NAVBAR_ITEM_EXITING : undefined}
            >
              <NavbarBackLink
                accessibilityLabel={formatMessage(messages.goBack)}
                onPress={handleBackPress}
              />
            </Animated.View>
          ) : undefined}

          {navbarRoutes.map((route) => (
            <Animated.View
              key={`${shouldRenderCompactNavbar ? 'compact' : 'full'}-${route.key}`}
              entering={shouldAnimateNavbar ? NAVBAR_ITEM_ENTERING : undefined}
              exiting={shouldAnimateNavbar ? NAVBAR_ITEM_EXITING : undefined}
              style={shouldRenderCompactNavbar ? styles.linkContainerCompact : styles.linkContainer}
            >
              <NavbarLink
                route={route}
                activeTabIndexSV={activeTabIndexSV}
                isActive={route.name === activeTabName}
                isLiquidGlassEnabled={isLiquidGlassEnabled}
                onAccountPress={handleAccountPress}
                onHomePress={handleHomePress}
                onMenuPress={handleMenuPress}
              />
            </Animated.View>
          ))}
        </Navbar.LinksGroup>

        <Navbar.CtaContainer>
          {hasActiveBag ? <NavbarBagButton isLiquidGlassEnabled={isLiquidGlassEnabled} /> : null}

          {shouldRenderOrderCta ? (
            <Navbar.Button
              ref={startMenuButtonRef}
              aria-expanded={shouldRenderStartMenu ? isStartMenuVisible : undefined}
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              title={formatMessage(orderCtaMessage)}
              onPress={handleStartOrderPress}
            />
          ) : null}
        </Navbar.CtaContainer>
      </Navbar.Container>

      {isGroupOrderingEnabled ? (
        <NavbarStartMenu
          isLiquidGlassEnabled={isLiquidGlassEnabled}
          returnFocusRef={startMenuButtonRef}
          visible={isStartMenuVisible}
          onClose={closeStartMenu}
        />
      ) : null}
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function navigateToTab(params: NavigateToTabParams) {
  const { descriptors, emitter, navigateToTab: navigateToRoute, state, tabName } = params;

  const route = state.routes.find((stateRoute) => stateRoute.name === tabName);

  if (!route) {
    return;
  }

  const event = emitter.emit({ type: 'tabPress', target: route.key, canPreventDefault: true });

  if (event.defaultPrevented) {
    return;
  }

  const options = descriptors[route.key].options as TabOptions;

  options.onPress?.();
  navigateToRoute(route.key);
}

/**
 * Tab bar routes no longer type their nested navigator state, but Expo Router
 * still attaches it at runtime.
 */
function getNestedRouteCount(route?: TabRoute) {
  const nestedState = route && 'state' in route ? route.state : undefined;

  if (!checkIfNestedNavigationState(nestedState)) {
    return 0;
  }

  return nestedState.routes.length;
}

function checkIfNestedNavigationState(value: unknown): value is NestedNavigationState {
  const isObject = typeof value === 'object' && value !== null;

  return isObject && 'routes' in value && Array.isArray(value.routes);
}

function getOrderCtaMessage(params: GetOrderCtaMessageParams) {
  const { isGroupOrderingEnabled, isStartMenuVisible } = params;

  if (isStartMenuVisible) {
    return messages.close;
  }

  if (isGroupOrderingEnabled) {
    return messages.start;
  }

  return messages.startOrder;
}

function checkIfHomeRoute(route: TabRoute) {
  return route.name === TABS.HOME;
}

function checkIfNavbarRoute(route: TabRoute) {
  return NAVBAR_TAB_NAMES.some((tabName) => tabName === route.name);
}

function getActiveTabIndex(routeName?: string) {
  return NAVBAR_TAB_NAMES.findIndex((tabName) => tabName === routeName);
}

function getActiveTabName(params: GetActiveTabNameParams) {
  const { segments, stateActiveTabName } = params;

  if (segments.some((segment) => segment === TABS.ACCOUNT)) {
    return TABS.ACCOUNT;
  }

  if (segments.some((segment) => segment === TABS.MENU || segment === TABS.MENU_SINGLE)) {
    return TABS.MENU;
  }

  if (segments.some((segment) => segment === TABS.REWARDS)) {
    return TABS.REWARDS;
  }

  if (segments.some((segment) => segment === TABS.HOME || segment === TABS.HOME_SINGLE)) {
    return TABS.HOME;
  }

  return stateActiveTabName;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const NAVBAR_LAYOUT_TRANSITION = LinearTransition.duration(theme.transitions.long).easing(
  Easing.bezier(0.2, 0, 0, 1),
);

const NAVBAR_BACK_ENTERING = FadeInLeft.duration(theme.transitions.base);
const NAVBAR_ITEM_ENTERING = FadeIn.duration(theme.transitions.base);
const NAVBAR_ITEM_EXITING = FadeOut.duration(theme.transitions.base);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  linksGroup: {
    flex: 1,
    maxWidth: 320,
  },
  linkContainerCompact: {
    minWidth: 60,
  },
  linkContainer: {
    flexBasis: 0,
    minWidth: 60,
    flexGrow: 1,
    flexShrink: 1,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  tabBarProps: TabBarProps;
};

type TabBarProps = Parameters<NonNullable<ComponentProps<typeof Tabs>['tabBar']>>[0];
type TabRoute = TabBarProps['state']['routes'][number];
type TabOptions = { onPress?: () => void };
type NestedNavigationState = { routes: readonly unknown[] };

type NavigateToTabParams = TabBarProps & { tabName: NavbarTabName };
type NavbarTabName = typeof TABS.HOME | typeof TABS.MENU | typeof TABS.ACCOUNT;
type GetActiveTabNameParams = { segments: readonly string[]; stateActiveTabName?: string };
type GetOrderCtaMessageParams = { isGroupOrderingEnabled: boolean; isStartMenuVisible: boolean };
type MenuView = (typeof MENU_VIEWS)[keyof typeof MENU_VIEWS];
type MenuSearchParams = { category?: string | string[]; view?: MenuView | MenuView[] };
